import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnFiltersState, createColumnHelper } from '@tanstack/react-table';
import { OrderListItem } from 'models/Order';
import { getOrdersAPI } from 'services/ordersApi';
import { GlobalSearch } from 'components/GlobalSearch';
import { Filter } from 'components/Filter/Filter';
import { FilterContainer } from 'components/Filter/FilterContainer';
import { DashboardSkeleton } from 'components/Skeleton/DashboardSkeleton';
import { CustomTooltip } from 'components/CustomTooltip';
import { toastWarning } from 'utils/toastMethod';
import CustomTable from 'components/Table/CustomTable';
import PageLayout from 'components/Layout/PageLayout';
import PageLayoutSkeleton from 'components/Layout/PageLayout/skeleton';

type FilterOptions = {
  status: string[];
  customerType: string[];
};

const currencyFormatter = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

const formatCurrency = (cents: number) => currencyFormatter.format(cents / 100);

const getCustomerType = (order: OrderListItem) => (order.isBusinessPurchase ? 'Azienda' : 'Privato');

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('it-IT', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

export default function OrdersTable() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderListItem[]>();
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ status: [], customerType: [] });

  useEffect(() => {
    getOrdersAPI()
      .then((orders: OrderListItem[]) => {
        setOrders(orders);
        const temp: FilterOptions = { status: [], customerType: [] };
        orders.forEach((order) => {
          if (!temp.status.includes(order.status)) temp.status.push(order.status);
          const customerType = getCustomerType(order);
          if (!temp.customerType.includes(customerType)) temp.customerType.push(customerType);
        });
        setFilterOptions(temp);
      })
      .catch(() => toastWarning('Qualcosa è andato storto!'));
  }, []);

  const setFilter = (columnId: string, filterValue: string) => {
    setColumnFilters((oldFilters) => {
      const updatedFilters = oldFilters.filter((filter) => filter.id !== columnId);
      if (filterValue) updatedFilters.push({ id: columnId, value: filterValue });
      return updatedFilters;
    });
  };

  const columnHelper = createColumnHelper<OrderListItem>();
  const columns = [
    columnHelper.accessor('orderNumber', {
      header: () => 'N. ORDINE',
      cell: (orderNumber) => orderNumber.getValue(),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor('customerInfo', {
      header: () => 'CLIENTE',
      cell: (customerInfo) => customerInfo.getValue(),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor((order) => getCustomerType(order), {
      id: 'customerType',
      header: () => 'TIPOLOGIA',
      cell: (customerType) => customerType.getValue(),
      enableSorting: false,
    }),
    columnHelper.accessor('totalAmount', {
      header: () => 'TOTALE',
      cell: (total) => formatCurrency(total.getValue() as number),
      enableColumnFilter: false,
      enableSorting: true,
      sortingFn: 'basic',
    }),
    columnHelper.accessor('status', {
      header: () => 'STATO',
      cell: (status) => status.getValue(),
      enableSorting: false,
    }),
    columnHelper.accessor('createdDate', {
      header: () => 'DATA ORDINE',
      cell: (created) => formatDate(created.getValue() as string),
      enableColumnFilter: false,
      enableSorting: true,
      sortingFn: (rowA, rowB, columnId) => {
        const dateA = new Date(rowA.getValue(columnId) as string);
        const dateB = new Date(rowB.getValue(columnId) as string);
        return dateA.getTime() - dateB.getTime();
      },
    }),
    columnHelper.display({
      header: 'AZIONI',
      cell: ({ row }) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ cursor: 'pointer' }} onClick={() => navigate(`/orders/${row.original.id}`)}>
            <CustomTooltip id={`OrderDetailIcon-${row.original.id}`} message="Visualizza dettaglio">
              <span style={{ fontSize: '18px' }}>&#128065;</span>
            </CustomTooltip>
          </div>
        </div>
      ),
    }),
  ];

  return (
    <>
      {orders ? (
        <PageLayout titlePage="Lista ordini">
          {orders.length === 0 ? (
            <h4>Non sono presenti ordini</h4>
          ) : (
            <>
              <GlobalSearch filterValue={globalFilter} onChange={(value) => setGlobalFilter(value)} />
              <FilterContainer>
                <Filter columnId="status" options={filterOptions.status} placeholder="Stato" setFilter={setFilter} />
                <Filter
                  columnId="customerType"
                  options={filterOptions.customerType}
                  placeholder="Tipologia"
                  setFilter={setFilter}
                />
              </FilterContainer>
              <CustomTable
                data={orders}
                columns={columns}
                pageStartIndex={0}
                pageMaxSize={10}
                columnsVisibility={{}}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
              />
            </>
          )}
        </PageLayout>
      ) : (
        <PageLayoutSkeleton>
          <DashboardSkeleton />
        </PageLayoutSkeleton>
      )}
    </>
  );
}
